<template>
    <section>
    <div v-if="isOpen" class="guest-checkout-container shipping-method">
        <div class="flex flex-row" style="justify-content: space-between;">
            <p class="title-l text-rothcoBlack">Shipping Method</p>
            <div v-if="props.user?.email" class="bg-rothcoCream accordion-button" @click="isOpen = false">
                <AccordionArrow class="icon-open"/>
            </div>
        </div>
        <div v-if="isCartOverThreshold" class="freight-notice">
            <p class="paragraph-details">For expedited shipping options on orders over ${{ expeditedShippingThreshold }}, please contact customer service <a href="mailto:CustomerService@rothco.com" class="freight-email-link" aria-label="Email Customer Service">customerservice@rothco.com</a> for a freight estimate.</p>
        </div>
        <div class="radio-group" role="radiogroup" :aria-required="true">
            <label :id="`shipping_method_option_${shippingMethod.name}`" class="option" v-for="(shippingMethod, index) in shippingMethodContent" :key="index" @click="modelValue = shippingMethod">
                <input type="radio" name="shipping_method" id="shipping_method_1" :value="shippingMethod.name" :checked="modelValue.name == shippingMethod.name" :code="shippingMethod.code">
                <div class="label-container">
                    <span class="paragraph-details semibold">{{ shippingMethod.name }}</span>
                    <p  v-show="false" class="paragraph-details estimation">Estimated delivery by {{ shippingMethod.formattedETA }}</p>
                </div>
                <p class="text-rothcoBlack shipping-method-price paragraph-details semibold">
                    {{ shippingMethod.formattedCost }}
                </p>
            </label>
        </div>
    </div>
    <div v-else class="guest-checkout-container shipping-method">
        <div class="flex flex-row" style="justify-content: space-between;">
            <p class="title-l text-rothcoBlack">Shipping Method</p>
            <div v-if="props.user?.email" class="bg-rothcoCream accordion-button" @click="isOpen = true">
                <AccordionArrow />
            </div>
        </div>
        <div>
            <div class="label-container">
                <span class="paragraph-details semibold">{{ selectedShippingMethod.name }}</span>
                <p v-show="false" class="paragraph-details estimation">Estimated delivery by {{ selectedShippingMethod.formattedETA }}</p>
            </div>
        </div>
    </div>
    </section>
</template>
<script lang="ts" setup>
    import { useDateFormat } from "@vueuse/core";
    import { v4 as uuidv4 } from "uuid";
    import { computed, ref, watch } from "vue";
    import type { PropType } from "vue";
    import { usePage } from "@inertiajs/vue3";
    import AccordionArrow from "@icons/accordion-arrow.svg";


    interface ShippingMethod {
        name: string;
        eta?: string;
        formattedETA: string;
        cost?: number;
        formattedCost?: string;
    }

    let props = defineProps({
        shippingMethods: {
            type: Array<ShippingMethod>,
            required: true,
            default: [{ name: "" }]
        },
        user: {
            type: Object,
            default() {
                return {};
            }
        },
        cartTotal: {
            type: Number,
            default: 0
        }
    });

    const expeditedShippingThreshold = usePage().props.d2c_expedited_shipping_max_cart_total;

    let isOpen = ref(true);

    let isCartOverThreshold = computed(() => {
        return props.cartTotal > expeditedShippingThreshold;
    });

    let shippingMethodContent = computed(() => {
        return props.shippingMethods.map((method) => {
            let eta = method.eta ? new Date(method.eta) : null;
            method.formattedETA = eta
                ? `ETA: ${useDateFormat(eta, "MM/DD").value.replace(/\"/g, "")}`
                : "XX/XX";
            method.formattedCost =
                method.cost && method.cost > 0
                    ? `$${parseFloat(method.cost.toString()).toFixed(2)}`
                    : "FREE";
            return method;
        });
    });

    let modelValue = defineModel("selectedShippingMethod", {
        type: Object as PropType<ShippingMethod>,
        default: { name: "" }
    });

    let emits = defineEmits(["shippingUpdated"]);

    watch(()=>props.shippingMethods, (newVal)=>{
        let result = newVal.find(method=>method.name == modelValue.value.name)
        if(result != null){
            modelValue.value.cost = result.cost;
        }
    }, {deep: true});

    watch(modelValue, (newVal) => {
        emits("shippingUpdated");
    });
</script>
<style lang="scss" scoped>
.icon-open {
    transform: scaleY(-1);
    -moz-transform: scaleY(-1);
    -webkit-transform: scaleY(-1);
    -ms-transform: scaleY(-1);
}
.accordion-button {
    padding: 0.375rem 0.8rem; 
    height: 50%; 
    cursor: pointer; 
    border-radius: 0.25rem;
}
.freight-notice {
    background-color: #fff3cd;
    border: 1px solid #ffc107;
    border-radius: 0.25rem;
    padding: 0.75rem;
    margin-bottom: 1rem;
    
    p {
        margin: 0;
        color: #856404;
        
        a {
            color: #856404;
            font-weight: 600;
            text-decoration: underline;
            
            &:hover {
                text-decoration: none;
            }
        }
    }
}
</style>
